export interface EstimateExportItem {
  code: string;
  description: string;
  category?: string;
  quantity?: number;
  standardPrice?: number;  // FS Base Rate
  fsAdjustment?: number;   // FS Adjustment
  unitPrice: number;       // Provider Rate
  gfeAdjustment?: number;  // GFE Adjustment
  patientCharges?: number; // Patient Charges
  providerId?: string;
  providerName?: string;
  providerNpi?: string;
  providerTaxId?: string;
}

export interface ProviderGFEGroup {
  providerId: string;
  providerName: string;
  providerAddress?: string;
  providerNpi?: string;
  providerTaxId?: string;
  items: EstimateExportItem[];
}

export interface EstimateExportData {
  title?: string;
  patientName?: string;
  patientEmail?: string;
  patientMemberId?: string;
  patientDob?: string;
  providerName?: string;
  providerAddress?: string;
  providerPhone?: string;
  providerNpi?: string;
  providerTaxId?: string;
  date?: string;
  serviceRenderedDate?: string;
  items: EstimateExportItem[];
  totalFsBaseRate?: number;
  totalFsAdjustment?: number;
  totalProviderCharges?: number;
  totalGfeAdjustment?: number;
  totalCharges?: number;
  discount?: number;
  finalAmount?: number;
  status?: string;
  providerGroups?: ProviderGFEGroup[];
}

export function exportEstimateToPDF(data: EstimateExportData) {
  if (typeof window === 'undefined') return;

  const dateStr = data.date
    ? new Date(data.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
    : new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  
  const patientName = data.patientName || "Patient / Recipient";
  const mainTitle = data.title || "Good Faith Cost Estimate (GFE) Medical Invoice";

  // Determine if we have multiple provider groups
  let groups: ProviderGFEGroup[] = data.providerGroups && data.providerGroups.length > 0
    ? data.providerGroups
    : [];

  if (groups.length === 0) {
    // Check if items contain distinct providerIds or providerNames
    const map = new Map<string, ProviderGFEGroup>();
    data.items.forEach(item => {
      const pid = item.providerId || 'main';
      const pname = item.providerName || data.providerName || "Healthcare Provider";
      if (!map.has(pid)) {
        map.set(pid, {
          providerId: pid,
          providerName: pname,
          providerAddress: data.providerAddress,
          providerNpi: item.providerNpi || data.providerNpi || '1928374650',
          providerTaxId: item.providerTaxId || data.providerTaxId || 'XX-XXX4920',
          items: []
        });
      }
      map.get(pid)!.items.push(item);
    });

    groups = Array.from(map.values());
  }

  // If fallback to 1 main group
  if (groups.length === 0) {
    groups = [{
      providerId: 'main',
      providerName: data.providerName || "Healthcare Provider",
      providerAddress: data.providerAddress,
      providerNpi: data.providerNpi || '1928374650',
      providerTaxId: data.providerTaxId || 'XX-XXX4920',
      items: data.items
    }];
  }

  // Build HTML for each provider GFE invoice
  const gfeInvoicesHtml = groups.map((grp, gIndex) => {
    const grpItemsHtml = grp.items.map(item => {
      const qty = item.quantity || 1;
      const stdPrice = Number(item.standardPrice || item.unitPrice || 0);
      const provRate = Number(item.unitPrice || 0);
      const fsAdj = Math.max(0, stdPrice - provRate);
      const gfeAdj = Number(item.gfeAdjustment || 0);
      const patCharge = item.patientCharges !== undefined ? Number(item.patientCharges) : Math.max(0, provRate - gfeAdj) * qty;

      return `
        <tr style="border-bottom: 1px solid #e2e8f0;">
          <td style="padding: 10px 10px; font-weight: 700; font-family: monospace; font-size: 12px; color: #2563eb;">${item.code}</td>
          <td style="padding: 10px 10px; font-size: 12px; color: #334155;">${item.description}</td>
          <td style="padding: 10px 10px; font-size: 11px; color: #64748b;">${item.category || 'General'}</td>
          <td style="padding: 10px 10px; text-align: right; font-size: 12px; color: #64748b;">$${stdPrice.toFixed(2)}</td>
          <td style="padding: 10px 10px; text-align: right; font-size: 12px; color: #16a34a;">-$${fsAdj.toFixed(2)}</td>
          <td style="padding: 10px 10px; text-align: right; font-weight: 700; font-size: 12px; color: #0f172a;">$${provRate.toFixed(2)}</td>
          <td style="padding: 10px 10px; text-align: right; font-size: 12px; color: #9333ea;">-$${gfeAdj.toFixed(2)}</td>
          <td style="padding: 10px 10px; text-align: right; font-weight: 800; font-size: 13px; color: #15803d;">$${patCharge.toFixed(2)}</td>
        </tr>
      `;
    }).join('');

    const grpFsBase = grp.items.reduce((s, i) => s + (i.standardPrice || i.unitPrice || 0) * (i.quantity || 1), 0);
    const grpFsAdj = grp.items.reduce((s, i) => s + Math.max(0, (i.standardPrice || i.unitPrice || 0) - i.unitPrice) * (i.quantity || 1), 0);
    const grpProvRate = grp.items.reduce((s, i) => s + (i.unitPrice || 0) * (i.quantity || 1), 0);
    const grpGfeAdj = grp.items.reduce((s, i) => s + (i.gfeAdjustment || 0) * (i.quantity || 1), 0);
    const grpPatientTotal = grp.items.reduce((s, i) => s + (i.patientCharges ?? Math.max(0, i.unitPrice - (i.gfeAdjustment || 0)) * (i.quantity || 1)), 0);

    return `
      <div class="invoice-container" style="${gIndex > 0 ? 'page-break-before: always; margin-top: 40px;' : ''}">
        <div class="invoice-header">
          <div>
            <div class="brand-title">StraightDoc Healthcare</div>
            <div class="subtitle">Official Good Faith Estimate Invoice ${groups.length > 1 ? `(Provider ${gIndex + 1} of ${groups.length})` : ''}</div>
          </div>
          <div style="text-align: right;">
            <div style="font-weight: 800; font-size: 18px; color: #0f172a;">${mainTitle}</div>
            <div style="font-size: 13px; color: #64748b; margin-top: 4px; font-weight: 500;">Estimate Date: ${dateStr}</div>
            ${data.serviceRenderedDate ? `<div style="font-size: 13px; color: #16a34a; margin-top: 2px; font-weight: 700;">Date Service Rendered: ${new Date(data.serviceRenderedDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}</div>` : ''}
          </div>
        </div>

        <div class="info-grid">
          <div class="info-item">
            <div class="info-label">Provider Information</div>
            <div class="info-value">${grp.providerName}</div>
            ${grp.providerAddress ? `<div style="font-size: 13px; color: #475569; margin-top: 2px;">${grp.providerAddress}</div>` : ''}
            <div style="font-size: 12px; color: #1e293b; margin-top: 8px; font-weight: 700; background: #e2e8f0; padding: 4px 8px; border-radius: 6px; display: inline-block;">
              NPI: ${grp.providerNpi || '1928374650'} &nbsp;|&nbsp; Tax ID: ${grp.providerTaxId || 'XX-XXX4920'}
            </div>
          </div>
          <div class="info-item">
            <div class="info-label">Patient / Member Information</div>
            <div class="info-value">${patientName}</div>
            ${data.patientEmail ? `<div style="font-size: 13px; color: #475569; margin-top: 3px;"><strong>Email:</strong> ${data.patientEmail}</div>` : ''}
            ${data.patientMemberId ? `<div style="font-size: 13px; color: #475569; margin-top: 3px;"><strong>Member ID:</strong> ${data.patientMemberId}</div>` : ''}
            ${data.patientDob ? `<div style="font-size: 13px; color: #475569; margin-top: 3px;"><strong>DOB:</strong> ${data.patientDob}</div>` : ''}
          </div>
        </div>

        <table>
          <thead>
            <tr>
              <th style="width: 12%;">Billing Code</th>
              <th style="width: 25%;">Description</th>
              <th style="width: 11%;">Code Category</th>
              <th style="width: 13%; text-align: right;">FS Base Rate</th>
              <th style="width: 13%; text-align: right;">FS Adjustment</th>
              <th style="width: 12%; text-align: right;">Provider Rate</th>
              <th style="width: 13%; text-align: right;">GFE Adjustment</th>
              <th style="width: 15%; text-align: right;">Patient Charges</th>
            </tr>
          </thead>
          <tbody>
            ${grpItemsHtml}
          </tbody>
        </table>

        <div class="summary-box">
          <div class="summary-row">
            <span>FS Base Rate Total:</span>
            <span style="font-weight: 600;">$${grpFsBase.toFixed(2)}</span>
          </div>
          <div class="summary-row" style="color: #16a34a;">
            <span>FS Adjustment Total:</span>
            <span style="font-weight: 600;">-$${grpFsAdj.toFixed(2)}</span>
          </div>
          <div class="summary-row">
            <span>Provider Rate Total:</span>
            <span style="font-weight: 700;">$${grpProvRate.toFixed(2)}</span>
          </div>
          <div class="summary-row" style="color: #9333ea;">
            <span>GFE Adjustment Total:</span>
            <span style="font-weight: 600;">-$${grpGfeAdj.toFixed(2)}</span>
          </div>
          <div class="summary-row total">
            <span>Patient Responsibility (Amount Due):</span>
            <span>$${grpPatientTotal.toFixed(2)}</span>
          </div>
        </div>

        <div class="disclaimer">
          <strong>Notice & Price Transparency Disclaimer:</strong> This Good Faith Estimate (GFE) invoice shows the costs of items and services that are reasonably expected for your health care needs under the No Surprises Act. This estimate is based on provider fee schedules known at creation time and does not include unknown or emergency costs that may arise during medical treatment. Each provider involved in your care bills separately under their respective NPI and Tax ID.
        </div>
      </div>
    `;
  }).join('');

  const htmlContent = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>${mainTitle}</title>
      <style>
        @media print {
          body { margin: 0; padding: 20px; }
          .no-print { display: none !important; }
        }
        body {
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
          color: #0f172a;
          margin: 0;
          padding: 40px;
          background: #ffffff;
          line-height: 1.5;
        }
        .invoice-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          border-bottom: 3px solid #2563eb;
          padding-bottom: 24px;
          margin-bottom: 28px;
        }
        .brand-title {
          font-size: 28px;
          font-weight: 900;
          color: #2563eb;
          letter-spacing: -0.5px;
        }
        .subtitle {
          font-size: 14px;
          color: #475569;
          margin-top: 4px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .info-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 24px;
          background: #f8fafc;
          padding: 24px;
          border-radius: 12px;
          border: 1px solid #cbd5e1;
          margin-bottom: 28px;
        }
        .info-item { font-size: 14px; }
        .info-label {
          font-weight: 800;
          color: #475569;
          text-transform: uppercase;
          font-size: 11px;
          letter-spacing: 0.5px;
          margin-bottom: 6px;
        }
        .info-value {
          font-weight: 800;
          color: #0f172a;
          font-size: 16px;
          margin-bottom: 4px;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 28px;
        }
        th {
          background: #f1f5f9;
          color: #334155;
          font-size: 11px;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          padding: 12px 10px;
          text-align: left;
          border-bottom: 2px solid #94a3b8;
        }
        .summary-box {
          width: 440px;
          margin-left: auto;
          background: #f8fafc;
          border: 2px solid #cbd5e1;
          border-radius: 12px;
          padding: 20px 24px;
          margin-bottom: 28px;
        }
        .summary-row {
          display: flex;
          justify-content: space-between;
          font-size: 14px;
          padding: 6px 0;
          color: #334155;
        }
        .summary-row.total {
          border-top: 2px solid #0f172a;
          padding-top: 12px;
          margin-top: 10px;
          font-size: 18px;
          font-weight: 900;
          color: #15803d;
        }
        .disclaimer {
          font-size: 12px;
          color: #475569;
          line-height: 1.6;
          border-top: 1px solid #e2e8f0;
          padding-top: 20px;
          margin-top: 32px;
        }
        .btn-print {
          background: #2563eb;
          color: white;
          border: none;
          padding: 12px 26px;
          border-radius: 8px;
          font-weight: 700;
          cursor: pointer;
          font-size: 15px;
          margin-bottom: 24px;
          box-shadow: 0 4px 12px rgba(37,99,235,0.35);
        }
      </style>
    </head>
    <body>
      <div class="no-print" style="text-align: right;">
        <button class="btn-print" onclick="window.print()">Print / Save PDF Invoice</button>
      </div>
      ${gfeInvoicesHtml}
      <script>
        window.onload = function() {
          setTimeout(function() {
            window.print();
          }, 300);
        };
      </script>
    </body>
    </html>
  `;

  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(htmlContent);
    printWindow.document.close();
  }
}

export function exportEstimateToCSV(data: EstimateExportData) {
  if (typeof window === 'undefined') return;

  let csv = "";
  if (data.serviceRenderedDate) {
    csv += `Date Service Rendered,${new Date(data.serviceRenderedDate).toLocaleDateString('en-US')}\n`;
  }
  csv += "Billing Code,Description,Code Category,FS Base Rate ($),FS Adjustment ($),Provider Rate ($),GFE Adjustment ($),Patient Charges ($)\n";
  data.items.forEach(l => {
    const qty = l.quantity || 1;
    const stdPrice = Number(l.standardPrice || l.unitPrice || 0);
    const provRate = Number(l.unitPrice || 0);
    const fsAdj = Math.max(0, stdPrice - provRate);
    const gfeAdj = Number(l.gfeAdjustment || 0);
    const patCharge = l.patientCharges !== undefined ? Number(l.patientCharges) : Math.max(0, provRate - gfeAdj) * qty;

    csv += `"${l.code}","${(l.description || '').replace(/"/g, '""')}","${l.category || 'General'}","$${stdPrice.toFixed(2)}","-$${fsAdj.toFixed(2)}","$${provRate.toFixed(2)}","-$${gfeAdj.toFixed(2)}","$${patCharge.toFixed(2)}"\n`;
  });

  const totalFsBase = data.totalFsBaseRate ?? data.items.reduce((s, i) => s + (i.standardPrice || i.unitPrice || 0) * (i.quantity || 1), 0);
  const totalFsAdj = data.totalFsAdjustment ?? data.items.reduce((s, i) => s + Math.max(0, (i.standardPrice || i.unitPrice || 0) - i.unitPrice) * (i.quantity || 1), 0);
  const totalProv = data.totalProviderCharges ?? data.items.reduce((s, i) => s + (i.unitPrice || 0) * (i.quantity || 1), 0);
  const totalGfeAdj = data.totalGfeAdjustment ?? data.items.reduce((s, i) => s + (i.gfeAdjustment || 0) * (i.quantity || 1), 0);
  const finalPatientTotal = data.finalAmount ?? data.items.reduce((s, i) => s + (i.patientCharges ?? (Math.max(0, i.unitPrice - (i.gfeAdjustment || 0)) * (i.quantity || 1))), 0);

  csv += `\nTotals Summary,,,,"$${totalFsBase.toFixed(2)}","-$${totalFsAdj.toFixed(2)}","$${totalProv.toFixed(2)}","-$${totalGfeAdj.toFixed(2)}","$${finalPatientTotal.toFixed(2)}"\n`;

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${(data.title || 'gfe-invoice').toLowerCase().replace(/[^a-z0-9]+/g, '-')}-${Date.now()}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
